import { useState } from "react";
import clsx from "clsx";
import { DateTime } from "luxon";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plane, PlaneTakeoff, Trash2, Warehouse } from "lucide-react";
import type { AirportConfig } from "@yb/core";
import { api, ApiError, type DayAhead, type FlightRow } from "../lib/api";
import { hhmm } from "../lib/format";
import { daysAheadSummary } from "../lib/days-ahead";
import { groupByDay, rangeEnd, SCHEDULE_MAX_DAYS } from "../lib/schedule";

/**
 * Every flight the board has for a run of days, grouped by day, with where each came
 * from: the live feed, a projection from a past weekday, or built here. Flights built
 * here and aircraft put into storage are added and removed on this screen; the feed's
 * own flights are listed but are not ours to delete.
 */

const input = "rounded-md border border-line bg-panel px-2 py-1.5 text-[12.5px]";
const th = "px-2 py-1.5 text-left text-[11px] font-semibold tracking-wide text-ink-3 uppercase";
const td = "px-2 py-1.5 align-middle";
const primary = "rounded-lg bg-ink px-3 py-1.5 text-[12.5px] font-bold text-panel disabled:opacity-60";
const secondary = "rounded-lg border border-line px-3 py-1.5 text-[12.5px] font-semibold hover:border-ink-3";

export function Schedule({ config, today, canEdit }: { config: AirportConfig; today: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const tz = config.timezone;
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(() => DateTime.fromISO(today, { zone: tz }).plus({ days: 7 }).toISODate()!);
  const [adding, setAdding] = useState<null | "flight" | "stored">(null);
  const end = rangeEnd(from, to);
  const last = DateTime.fromISO(from).plus({ days: SCHEDULE_MAX_DAYS - 1 }).toISODate()!;

  const list = useQuery({ queryKey: ["flights", from, end], queryFn: () => api.flights(from, end), placeholderData: (prev) => prev });
  const ahead = useQuery({ queryKey: ["days-ahead"], queryFn: api.daysAhead, refetchInterval: 60_000 });
  const done = () => {
    setAdding(null);
    void qc.invalidateQueries();
  };
  const remove = useMutation({
    mutationFn: (id: string) => api.removeScheduled(id),
    onSuccess: () => void qc.invalidateQueries(),
  });

  const days = groupByDay(list.data?.days ?? [], today, tz);
  return (
    <div className="scroll-thin h-full overflow-auto p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h1 className="mr-2 text-[18px] font-bold">Schedule</h1>
        {canEdit && (
          <>
            <button className={clsx(secondary, "flex items-center gap-1")} onClick={() => setAdding(adding === "flight" ? null : "flight")}>
              <PlaneTakeoff className="size-4" /> Build a flight
            </button>
            <button className={clsx(secondary, "flex items-center gap-1")} onClick={() => setAdding(adding === "stored" ? null : "stored")}>
              <Warehouse className="size-4" /> Add an aircraft in storage
            </button>
          </>
        )}
        <div className="ml-auto flex items-center gap-2 text-[12px]">
          <label className="flex items-center gap-1">
            From <input type="date" className={input} value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="flex items-center gap-1">
            To <input type="date" className={input} value={end} min={from} max={last} onChange={(e) => setTo(e.target.value)} />
          </label>
          <span className="text-ink-3">Up to {SCHEDULE_MAX_DAYS} days at a time</span>
        </div>
      </div>

      <p className="mb-3 max-w-3xl text-[12.5px] text-ink-3">
        Every flight the board has for these days, with where it came from. Flights built here show on the board straight away, with a stand, counters and belts; when
        the live feed later publishes the same flight, the feed&apos;s times take over. An aircraft in storage sits on a stand with nothing to operate until you give it a
        flight, usually through a tail swap.
      </p>

      {ahead.data && <DaysAheadStrip days={ahead.data.days} tz={tz} />}

      {adding === "flight" && <BuildFlight config={config} today={today} onDone={done} />}
      {adding === "stored" && <AddStored config={config} onDone={done} />}

      {list.isLoading ? (
        <Loader2 className="size-5 animate-spin text-ink-3" />
      ) : (
        days.map((day) => (
          <section key={day.date} className="mb-5">
            <h2 className="flex flex-wrap items-baseline gap-x-2 text-[14px] font-bold">
              {day.heading}
              <span className="text-[12px] font-normal text-ink-3">{day.count}</span>
            </h2>
            {day.note && <div className="mb-1 text-[12px] text-ink-3">{day.note}</div>}
            {day.rows.length === 0 ? (
              <p className="text-[12.5px] text-ink-3">Nothing on the board for this day yet.</p>
            ) : (
              <table className="w-full max-w-6xl text-[12.5px]">
                <thead>
                  <tr className="border-b border-line">
                    <th className={th}>Time</th>
                    <th className={th}>Flight</th>
                    <th className={th}>Arr / Dep</th>
                    <th className={th}>From / To</th>
                    <th className={th}>Airline</th>
                    <th className={th}>Type</th>
                    <th className={th}>Reg</th>
                    <th className={th}>Stand</th>
                    <th className={th}>Source</th>
                    <th className={th} />
                  </tr>
                </thead>
                <tbody>
                  {day.rows.map((r) => (
                    <FlightLine key={r.id} r={r} tz={tz} canEdit={canEdit} onRemove={() => remove.mutate(r.scheduledId!)} />
                  ))}
                </tbody>
              </table>
            )}
          </section>
        ))
      )}
    </div>
  );
}

/**
 * One line on the days ahead: planned or not, and what has no stand. Warn tone when
 * something needs a planner, ok tone otherwise; nothing more, because the question it
 * answers is a yes-or-no one. Its own component so the wording can be rendered by a test.
 */
export function DaysAheadStrip({ days, tz }: { days: DayAhead[]; tz: string }) {
  if (!days.length) return null;
  const { text, tone } = daysAheadSummary(days, tz);
  return (
    <div
      data-days-ahead={tone}
      className={clsx(
        "mb-3 inline-flex max-w-3xl items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[12px] font-semibold",
        tone === "ok" ? "border-ok/40 bg-ok/5 text-ok" : "border-warn/40 bg-warn/5 text-warn",
      )}
    >
      <span className="size-1.5 shrink-0 rounded-full bg-current" />
      {text}
    </div>
  );
}

function FlightLine({ r, tz, canEdit, onRemove }: { r: FlightRow; tz: string; canEdit: boolean; onRemove: () => void }) {
  const cancelled = r.status === "cancelled";
  return (
    <tr className={clsx("border-b border-line", cancelled && "text-ink-3")}>
      <td className={clsx(td, "tabular")}>
        {hhmm(r.scheduled, tz)}
        {cancelled && <span className="ml-1 text-bad">cancelled</span>}
      </td>
      <td className={clsx(td, "font-semibold")}>
        {r.stored ? (
          <span className="flex items-center gap-1">
            <Warehouse className="size-3.5 text-ink-3" /> {r.registration ?? r.flightNumber}
          </span>
        ) : (
          <span className="flex items-center gap-1">
            <Plane className={clsx("size-3.5 text-ink-3", r.direction === "ARR" && "rotate-90")} /> {r.flightNumber}
          </span>
        )}
      </td>
      <td className={td}>{r.stored ? "—" : r.direction === "ARR" ? "Arrival" : "Departure"}</td>
      <td className={td}>{r.stored ? "in storage" : (r.otherAirport ?? "—")}</td>
      <td className={td}>{r.airlineName}</td>
      <td className={td}>{r.aircraftType ?? "—"}</td>
      <td className={td}>{r.registration ?? "—"}</td>
      <td className={td}>{r.stand ?? "—"}</td>
      <td className={clsx(td, "text-ink-3")}>{r.label}</td>
      <td className={clsx(td, "text-right")}>
        {canEdit && r.scheduledId && (
          <button className={secondary} title="Remove from the schedule" onClick={onRemove}>
            <Trash2 className="size-4" />
          </button>
        )}
      </td>
    </tr>
  );
}

function BuildFlight({ config, today, onDone }: { config: AirportConfig; today: string; onDone: () => void }) {
  const [f, setF] = useState({
    airline: Object.keys(config.airlines)[0] ?? "",
    flightNumber: "",
    direction: "DEP" as "ARR" | "DEP",
    localTime: `${today}T12:00`,
    otherAirport: "",
    aircraftType: "",
    registration: "",
    sector: "D" as "D" | "T" | "I",
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));

  return (
    <form
      className="mb-4 flex flex-wrap items-end gap-2 rounded-xl border border-line p-3"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          await api.buildFlight({ ...f, seats: null, otherAirport: f.otherAirport || null, aircraftType: f.aircraftType || null, registration: f.registration || null });
          onDone();
        } catch (err) {
          setError(err instanceof ApiError ? err.message : "Could not build that flight");
        } finally {
          setBusy(false);
        }
      }}
    >
      <Field label="Airline">
        <select className={input} value={f.airline} onChange={(e) => set("airline", e.target.value)}>
          {Object.values(config.airlines).map((a) => (
            <option key={a.code} value={a.code}>
              {a.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Flight number">
        <input required className={clsx(input, "w-28 uppercase")} placeholder="WS123" value={f.flightNumber} onChange={(e) => set("flightNumber", e.target.value)} />
      </Field>
      <Field label="Direction">
        <select className={input} value={f.direction} onChange={(e) => set("direction", e.target.value as "ARR" | "DEP")}>
          <option value="DEP">Departure</option>
          <option value="ARR">Arrival</option>
        </select>
      </Field>
      <Field label="Local date and time">
        <input required type="datetime-local" className={input} value={f.localTime} onChange={(e) => set("localTime", e.target.value)} />
      </Field>
      <Field label={f.direction === "ARR" ? "From" : "To"}>
        <input className={clsx(input, "w-20 uppercase")} placeholder="YVR" value={f.otherAirport} onChange={(e) => set("otherAirport", e.target.value)} />
      </Field>
      <Field label="Aircraft">
        <select className={input} value={f.aircraftType} onChange={(e) => set("aircraftType", e.target.value)}>
          <option value="">Unknown</option>
          {Object.keys(config.aircraftTypes)
            .sort()
            .map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
        </select>
      </Field>
      <Field label="Tail">
        <input className={clsx(input, "w-28 uppercase")} placeholder="C-FABC" value={f.registration} onChange={(e) => set("registration", e.target.value)} />
      </Field>
      <Field label="Sector">
        <select className={input} value={f.sector} onChange={(e) => set("sector", e.target.value as "D" | "T" | "I")}>
          <option value="D">Domestic</option>
          <option value="T">Transborder</option>
          <option value="I">International</option>
        </select>
      </Field>
      <button disabled={busy} className={primary}>
        {busy ? "Adding…" : "Add to the schedule"}
      </button>
      {error && <div className="w-full text-[12.5px] text-bad">{error}</div>}
    </form>
  );
}

function AddStored({ config, onDone }: { config: AirportConfig; onDone: () => void }) {
  const [reg, setReg] = useState("");
  const [type, setType] = useState("");
  const [airline, setAirline] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <form
      className="mb-4 flex flex-wrap items-end gap-2 rounded-xl border border-line p-3"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          await api.addStoredAircraft({ registration: reg, aircraftType: type || null, airline: airline || null, note: note || undefined });
          onDone();
        } catch (err) {
          setError(err instanceof ApiError ? err.message : "Could not add that aircraft");
        } finally {
          setBusy(false);
        }
      }}
    >
      <Field label="Tail">
        <input required className={clsx(input, "w-32 uppercase")} placeholder="C-FABC" value={reg} onChange={(e) => setReg(e.target.value)} />
      </Field>
      <Field label="Aircraft">
        <select className={input} value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">Unknown</option>
          {Object.keys(config.aircraftTypes)
            .sort()
            .map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
        </select>
      </Field>
      <Field label="Airline">
        <select className={input} value={airline} onChange={(e) => setAirline(e.target.value)}>
          <option value="">Not known</option>
          {Object.values(config.airlines).map((a) => (
            <option key={a.code} value={a.code}>
              {a.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Note">
        <input className={clsx(input, "w-56")} placeholder="Why it is here" value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      <button disabled={busy} className={primary}>
        {busy ? "Adding…" : "Put into storage"}
      </button>
      {error && <div className="w-full text-[12.5px] text-bad">{error}</div>}
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="text-[11px] font-semibold text-ink-3">
      {label}
      <div className="mt-0.5">{children}</div>
    </label>
  );
}
